import { useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { ThemePalette } from 'mui.theme';
import ProductionSelector from 'views/Production/components/Selector/Selector';
import MultiColorCircularLoader from 'views/Production/components/BoxLoader/MultiColorCircularLoader';
import { useAppSelector } from 'store/hook';
import AssetItem from 'views/Production/AllFleet/components/LoaderAssetItem';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  title: string;
  isLoading: boolean;
  unitSystem: string;
  customerCode: string;
  isPrinting: boolean;
  aggregation: string;
}

const options = [
  'Production',
  'Production Per Hour',
  'Load Per Day',
  'Hauler Load Time',
  'Loader Cycle Time',
];

const sortOptions = ['Least Load Per Day', 'Most Load Per Day'];

const LoaderAssetWrapper = ({
  enableDarkTheme,
  title,
  isLoading,
  unitSystem,
  customerCode,
  isPrinting,
  aggregation,
}: Props) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [selectedSource, setSelectedSource] = useState<string>('Load Per Day');
  const [selectedSort, setSelectedSort] = useState<string>('Most Load Per Day');

  const { assets } = useAppSelector((state) => state.assetReducer);
  const {
    data: {
      loaders: { deviceSummary },
    },
  } = useAppSelector((state) => state.productionReducer);

  const formattedData = deviceSummary
    ?.filter((sumItem) => sumItem.device_loads_per_day !== 0)
    ?.map((item) => {
      const assetDetail = assets.find(
        (itemData) => itemData.device.deviceId === item.loader
      );
      return {
        ...item,
        loadPerDay: Number(item.device_loads_per_day),
        production: Number(item.total_load_for_selected_range),
        haulerLoadTime: Number(item.device_hauler_load_time),
        productionRatePerHour: Number(item.device_loads_per_hr),
        loaderCycleTime: Number(item.device_loader_cycle_time),
        bumper_id: assetDetail?.bumperNumber,
        make: assetDetail?.make,
        model: assetDetail?.model,
      };
    });

  const sortedData = formattedData?.sort((a, b) => {
    if (selectedSort === 'Least Load Per Day') {
      return a.loadPerDay - b.loadPerDay;
    }
    return b.loadPerDay - a.loadPerDay;
  });

  return (
    <Grid item lg={6} md={12}>
      <Box
        sx={{
          padding: 2,
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.boxBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.cardBorderColor}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: '600',
                paddingTop: '5px',
              }}
            >
              {title}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: '600',
                color: palette.textSecondary,
                paddingRight: '15px',
                paddingTop: '5px',
              }}
            >
              Show
            </Typography>

            <ProductionSelector
              options={options}
              selected={selectedSource}
              setSelected={setSelectedSource}
              minWidth="150px"
            />

            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: '600',
                color: palette.textSecondary,
                paddingLeft: '10px',
                paddingRight: '15px',
                paddingTop: '5px',
              }}
            >
              {t('sort_by_10')}
            </Typography>

            <ProductionSelector
              options={sortOptions}
              selected={selectedSort}
              setSelected={setSelectedSort}
              minWidth="150px"
            />
          </Box>
        </Box>
        <Box
          sx={{
            paddingTop: '20px',
          }}
        >
          {isLoading ? (
            <MultiColorCircularLoader height="200px" />
          ) : (
            <>
              {sortedData?.length < 1 ? (
                <Typography
                  sx={{
                    color: palette.textPrimary,
                    fontSize: '11px',
                  }}
                >
                  {t('no_assets_available_to_display')}
                </Typography>
              ) : (
                <>
                  {sortedData?.map((item, index) => {
                    return (
                      <div key={index}>
                        <AssetItem
                          enableDarkTheme={enableDarkTheme}
                          key={index}
                          selectedSource={selectedSource}
                          unitSystem={unitSystem}
                          item={item}
                          customerCode={customerCode}
                          aggregation={aggregation}
                        />
                      </div>
                    );
                  })}
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default LoaderAssetWrapper;
